// §D9 — Returns section of the main /dashboard Overview (supplier side). Same
// analytics endpoints as /returns/dashboard. The caller gates it on
// RETURN_ANALYTICS_VIEW (BE requires the same code) and shows a DeniedCard
// otherwise.
//
// Every request carries the page's selected month. Without it the endpoints
// fall back to their own defaults (dashboard / top-products 30 days,
// repeat-returners 90 days), which is how the buyer list used to show 9
// returns next to tiles that added up to 2.
import { Anchor, Box, Paper, Skeleton } from "@mantine/core"
import { ArrowCounterClockwiseIcon, CheckCircleIcon, PercentIcon, ReceiptIcon } from "@phosphor-icons/react"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import useSWR from "swr"
import { ReturnAnalyticsApi } from "../../../apis"
import { currencyFormat } from "../../../utils"
import { formatDuration } from "../../../utils/format-duration"
import { formatCurrencyShort } from "../utils"
import classes from "../real.module.css"
import { CardHeader } from "./CardHeader"
import { KpiTile, KpiTileProps } from "./KpiTile"
import { RankList } from "./RankList"

const DASH = "—"
const LIST_LIMIT = 5

type Range = { dateFrom: string; dateTo: string }

/** [start of month, start of next month) — the endpoints filter requested_at >= from AND < to. */
const monthRange = (m: dayjs.Dayjs): Range => ({
  dateFrom: m.startOf("month").toISOString(),
  dateTo: m.add(1, "month").startOf("month").toISOString(),
})

const pct = (rate: number) => (rate * 100).toFixed(1)

interface Props {
  /** Any day of the selected month (YYYY-MM-DD); null → the current month. */
  month: string | null
}

export function ReturnsOverview({ month }: Props) {
  const { t } = useTranslation(["dashboard", "returns"])
  const w = (k: string, opts?: Record<string, unknown>) => t(`dashboard:returnsWidget.${k}`, opts)

  const m = month ? dayjs(month) : dayjs()
  const range = monthRange(m)
  const prevRange = monthRange(m.subtract(1, "month"))

  // 30s auto-refresh on the headline figures, same cadence as /returns/dashboard.
  const { data: dash, error: dashError } = useSWR(
    ["returns.dashboard.analytics", range.dateFrom, range.dateTo],
    () => ReturnAnalyticsApi.dashboard(range),
    { refreshInterval: 30_000, revalidateOnFocus: false },
  )
  // Previous month: only its return rate is shown, as the line under the rate tile.
  const { data: prevDash } = useSWR(
    ["returns.dashboard.analytics", prevRange.dateFrom, prevRange.dateTo],
    () => ReturnAnalyticsApi.dashboard(prevRange),
    { revalidateOnFocus: false },
  )
  const { data: products } = useSWR(
    ["returns.top-products", range.dateFrom, range.dateTo, LIST_LIMIT],
    () => ReturnAnalyticsApi.topProducts({ ...range, limit: LIST_LIMIT }),
    { revalidateOnFocus: false },
  )
  const { data: buyers } = useSWR(
    ["returns.top-buyers", range.dateFrom, range.dateTo, LIST_LIMIT],
    () => ReturnAnalyticsApi.repeatReturners({ ...range, threshold: 1, limit: LIST_LIMIT }),
    { revalidateOnFocus: false },
  )

  const counts = dash?.counts
  const unit = t("dashboard:numberFormat.unit")
  const requested = formatCurrencyShort(dash?.money.totalRequested ?? 0)

  const tiles: (KpiTileProps & { id: string })[] = [
    {
      id: "active",
      icon: ArrowCounterClockwiseIcon,
      label: w("active"),
      helpKey: "order.dashboard.field.returns-active",
      value: counts ? counts.active.toLocaleString() : DASH,
      unit: counts ? unit : undefined,
      // «Хариу хүлээж буй» folded in here: it is the part of the active returns that needs us.
      foot: counts
        ? counts.staffPending > 0
          ? w("activeFoot", { count: counts.staffPending })
          : w("activeFootNone")
        : undefined,
    },
    {
      id: "resolved",
      icon: CheckCircleIcon,
      label: w("resolved"),
      helpKey: "order.dashboard.field.returns-resolved",
      value: counts ? counts.completed.toLocaleString() : DASH,
      unit: counts ? unit : undefined,
      foot: dash && dash.avgResolutionSeconds > 0
        ? w("resolvedFoot", { duration: formatDuration(dash.avgResolutionSeconds) })
        : undefined,
    },
    {
      id: "rate",
      icon: PercentIcon,
      label: w("returnRate"),
      helpKey: "order.dashboard.field.returns-rate",
      value: dash ? pct(dash.rates.returnRate) : DASH,
      unit: dash ? "%" : undefined,
      foot: prevDash ? w("returnRateFoot", { value: pct(prevDash.rates.returnRate) }) : undefined,
    },
    {
      // Requested amount (the return-rate numerator), with what has actually been
      // paid back underneath. The old tile showed only the COMPLETED refunds as
      // «Нийт буцаалтын дүн», so it read 0₮ beside a buyer row of 465,000₮.
      id: "amount",
      icon: ReceiptIcon,
      label: w("requested"),
      helpKey: "order.dashboard.field.returns-amount",
      value: dash ? requested.num : DASH,
      unit: dash ? requested.unit : undefined,
      foot: dash ? w("requestedFoot", { amount: currencyFormat(dash.money.totalRefunded) }) : undefined,
    },
  ]

  return (
    <Box>
      <CardHeader
        title={w("title")}
        helpKey="order.dashboard.section.returns-widget"
        subtitle={t("dashboard:selectedMonth")}
        right={
          <Anchor component={Link} to="/returns/list" className={classes.seeAll}>
            {t("dashboard:viewAll")}
          </Anchor>
        }
      />

      <Box className={classes.kpiRow}>
        {!dash && !dashError
          ? tiles.map((tile) => <Skeleton key={tile.id} h={122} radius="md" />)
          : tiles.map(({ id, ...tile }) => <KpiTile key={id} {...tile} />)}
      </Box>

      <Box className={classes.returnsLists}>
        <Paper className={classes.card}>
          <CardHeader title={t("returns:dashboard.topProducts.title")} subtitle={w("topProductsSub")} />
          <RankList
            items={(products?.products ?? []).map((p) => ({
              key: p.productId ?? p.productName,
              name: p.productName,
              count: p.returnCount,
              // returnCount is the number of return requests, not pieces: the old «2 ш» was wrong.
              value: w("times", { count: p.returnCount }),
            }))}
          />
        </Paper>
        <Paper className={classes.card}>
          <CardHeader title={t("returns:dashboard.topBuyers.title")} subtitle={w("topBuyersSub")} />
          <RankList
            items={(buyers?.buyers ?? []).map((b) => ({
              key: b.buyerBusinessId,
              name: b.buyerBusinessName ?? DASH,
              count: b.returnCount,
              value: w("times", { count: b.returnCount }),
            }))}
          />
        </Paper>
      </Box>
    </Box>
  )
}
